/**
 * Números dos cartões do Exterior (§8.5), funções puras: as cidades (do `StateDetail` de `ZZ`, com as chaves em código
 * TSE; enquanto ele não chega, as fatias das colunas da vista, `ResultsView.abroad`), a soma por país (no cliente,
 * §8.5 "Dados") e o andamento do fechamento das urnas, pelo instante exibido (`closes_t_min`, minutos de Brasília).
 *
 * Par sempre na ordem do contrato [A, B] = [13, 22]; o líder de um lugar é quem tem mais votos nele (dado), e empate
 * não tem líder.
 */
import type { StateDetail } from '../contracts/municipal';
import type { AbroadCity, CountryInfo } from '../contracts/static';
import { municipalityFromColumns, municipalityFromDetail } from './municipality';
import type { Candidate } from './types';

type Pair = readonly [Pick<Candidate, 'number'>, Pick<Candidate, 'number'>];

/** Uma cidade do exterior no instante. */
export interface CityRow {
  city: AbroadCity;
  /** As urnas já fecharam no instante exibido. */
  closed: boolean;
  /** Seções da cidade (do detalhe; sem ele, a de referência da geografia). */
  sections: number;
  /** Seções totalizadas; null sem o detalhe. */
  sectionsCounted: number | null;
  /** Votos de [A, B]; null sem o detalhe. */
  votes: [number, number] | null;
  /** Fatias dos válidos (do detalhe ou, sem ele, das colunas); null sem voto válido. */
  shares: [number, number] | null;
  /** Fração das seções totalizadas (0–1). */
  sectionsFraction: number;
}

/** As cidades do exterior no instante, na ordem de `cities`. */
export function cityRows(
  cities: readonly AbroadCity[],
  detail: StateDetail | null | undefined,
  cols: Float32Array | null | undefined,
  pair: Pair | null,
  tMin: number,
): CityRow[] {
  return cities.map((city) => {
    const n = pair ? municipalityFromDetail(detail, city.tse, pair) : null;
    const c = n ? null : municipalityFromColumns(cols, city.idx);
    const sections = n ? n.sections : city.sections_ref;
    const counted = n ? n.sectionsCounted : null;
    return {
      city,
      closed: tMin >= city.closes_t_min,
      sections,
      sectionsCounted: counted,
      votes: n && n.valid > 0 ? n.votes : null,
      shares: n ? n.shares : (c?.shares ?? null),
      sectionsFraction: n ? (sections > 0 ? (counted ?? 0) / sections : 0) : (c?.fraction ?? 0),
    };
  });
}

/** Um país no instante: a soma das cidades dele. */
export interface CountryRow {
  country: CountryInfo;
  cities: CityRow[];
  closed: boolean;
  /** Fechamento mais tardio entre as cidades ainda abertas; null com todas fechadas. */
  closesTMin: number | null;
  sectionsFraction: number;
  votes: [number, number] | null;
  shares: [number, number] | null;
}

export function countryRow(country: CountryInfo, cities: readonly CityRow[]): CountryRow {
  const mine = cities.filter((c) => c.city.country_iso2 === country.iso2);
  const open = mine.filter((c) => !c.closed);
  let sections = 0;
  let counted = 0;
  let fractionWeighted = 0;
  let electorate = 0;
  let a = 0;
  let b = 0;
  let anyVotes = false;
  let shareB = 0;
  let shareWeight = 0;
  for (const c of mine) {
    sections += c.sections;
    counted += c.sectionsCounted ?? 0;
    electorate += c.city.electorate_ref;
    fractionWeighted += c.sectionsFraction * c.city.electorate_ref;
    if (c.votes) {
      anyVotes = true;
      a += c.votes[0];
      b += c.votes[1];
    } else if (c.shares) {
      // sem o detalhe: a fatia do país pondera as fatias SÓ das cidades que têm fatia, pelo eleitorado já apurado delas
      // (aproximação até o detalhe chegar). Dividir pelo eleitorado do país inteiro dava ao 13 o resto das cidades sem
      // voto e trocava o líder (revisão do #85)
      const w = c.city.electorate_ref * Math.max(c.sectionsFraction, 0);
      shareB += c.shares[1] * w;
      shareWeight += w;
    }
  }
  const hasDetail = mine.some((c) => c.sectionsCounted !== null);
  const votes: [number, number] | null = anyVotes ? [a, b] : null;
  const valid = a + b;
  const shares: [number, number] | null =
    votes && valid > 0
      ? [a / valid, b / valid]
      : shareWeight > 0
        ? [1 - shareB / shareWeight, shareB / shareWeight]
        : null;
  return {
    country,
    cities: mine,
    closed: open.length === 0,
    closesTMin: open.length === 0 ? null : Math.max(...open.map((c) => c.city.closes_t_min)),
    sectionsFraction: hasDetail
      ? sections > 0
        ? counted / sections
        : 0
      : electorate > 0
        ? fractionWeighted / electorate
        : 0,
    votes,
    shares,
  };
}

/** Os países em ordem decrescente de eleitorado (AC-8.5-02). */
export function countryRows(countries: readonly CountryInfo[], cities: readonly CityRow[]): CountryRow[] {
  return [...countries]
    .sort((x, y) => y.electorate_ref - x.electorate_ref || x.name.localeCompare(y.name, 'pt-BR'))
    .map((c) => countryRow(c, cities));
}

/** O líder de uma linha (índice no par), ou null sem fatias ou com empate. */
export function leaderOf(shares: [number, number] | null): 0 | 1 | null {
  if (!shares || shares[0] === shares[1]) return null;
  return shares[0] > shares[1] ? 0 : 1;
}

/** "As urnas já fecharam em N de M cidades, com X dos Y eleitores do exterior" (§8.5). */
export function abroadClosing(cities: readonly CityRow[]): {
  closed: number;
  total: number;
  closedElectorate: number;
  electorate: number;
} {
  let closed = 0;
  let closedElectorate = 0;
  let electorate = 0;
  for (const c of cities) {
    electorate += c.city.electorate_ref;
    if (c.closed) {
      closed++;
      closedElectorate += c.city.electorate_ref;
    }
  }
  return { closed, total: cities.length, closedElectorate, electorate };
}

/** "Ainda votando": até `max` países de maior eleitorado com cidade aberta, cada um com o fechamento mais tardio. */
export function stillVoting(
  countries: readonly CountryRow[],
  max = 4,
): { items: Array<{ name: string; closesTMin: number }>; more: number } {
  const open = countries.filter((c) => !c.closed && c.closesTMin !== null);
  return {
    items: open.slice(0, max).map((c) => ({ name: c.country.name, closesTMin: c.closesTMin as number })),
    more: Math.max(0, open.length - max),
  };
}

/** A situação de uma linha sem votos (§8.5 CountryRow/CityRow, coluna 3). */
export type RowStatus = 'counted' | 'counting' | 'closed' | 'open';

export function rowStatus(sectionsFraction: number, closed: boolean): RowStatus {
  if (sectionsFraction >= 1) return 'counted';
  if (sectionsFraction > 0) return 'counting';
  return closed ? 'closed' : 'open';
}
